import * as Redux from '@reduxjs/toolkit';
import type { IManageLocations } from '~/appstate';
import * as Hub from '~/ogm-hub';

export interface ISyscomanageLocationState {
	currentPage: number;
	customerId: number;
	customerName: string;
	deleteModalOpen: boolean;
	isDeleting: boolean;
	isLoading: boolean;
	itemsPerPage: number;
	locations: IManageLocations[];
	selectedLocationIds: number[];
	totalLocations: number;
	totalPages: number;
}

export function makeInitialState(): ISyscomanageLocationState {
	return {
		currentPage: 1,
		customerId: 0,
		customerName: '',
		deleteModalOpen: false,
		isDeleting: false,
		isLoading: false,
		itemsPerPage: 20,
		locations: [],
		selectedLocationIds: [],
		totalLocations: 0,
		totalPages: 0,
	};
}

export const slice = Redux.createSlice({
	name: 'syscoManageLocations',
	initialState: makeInitialState(),
	reducers: {
		resetState(state) {
			const initialState = makeInitialState();
			state.currentPage = initialState.currentPage;
			state.customerId = initialState.customerId;
			state.customerName = initialState.customerName;
			state.deleteModalOpen = initialState.deleteModalOpen;
			state.isLoading = initialState.isLoading;
			state.itemsPerPage = initialState.itemsPerPage;
			state.locations = initialState.locations;
			state.selectedLocationIds = initialState.selectedLocationIds;
			state.totalLocations = initialState.totalLocations;
			state.totalPages = initialState.totalPages;
		},
		setCustomer(state, action) {
			state.customerId = action.payload.id;
			state.customerName = action.payload.name;
		},
		setCustomerName(state, action) {
			state.customerName = action.payload;
		},
		loadingLocations(state) {
			state.isLoading = true;
		},
		loadingLocations_SUCCESS(state, action) {
			state.isLoading = false;
			state.locations = action.payload.results;
			state.totalLocations = action.payload.totalResults;
			state.totalPages = action.payload.totalPages;
		},
		loadingLocations_ERROR(state) {
			state.deleteModalOpen = false;
			state.isDeleting = false;
			state.isLoading = false;
		},
		pageChange(state, action) {
			state.currentPage = action.payload;
		},
		itemsPerPageChange(state, action) {
			state.itemsPerPage = action.payload;
		},
		addSelectedLocationId(state, action) {
			if (
				state.selectedLocationIds.findIndex((x) => x === action.payload) === -1
			) {
				state.selectedLocationIds = state.selectedLocationIds.concat([
					action.payload,
				]);
			}
		},
		removeSelectedLocationId(state, action) {
			const index = state.selectedLocationIds.findIndex(
				(x) => x === action.payload
			);
			if (index > -1) {
				const clone = state.selectedLocationIds.splice(0);
				clone.splice(index, 1);
				state.selectedLocationIds = clone;
			}
		},
		openDeleteModal(state) {
			state.deleteModalOpen = true;
		},
		closeDeleteModal(state) {
			state.deleteModalOpen = false;
		},
		setIsDeleting(state, action) {
			state.isDeleting = action.payload;
		},
		resetAfterLocationsDeleted(state) {
			const initialState = makeInitialState();
			state.currentPage = initialState.currentPage;
			state.deleteModalOpen = initialState.deleteModalOpen;
			state.isDeleting = initialState.isDeleting;
			state.locations = initialState.locations;
			state.selectedLocationIds = initialState.selectedLocationIds;
			state.totalLocations = initialState.totalLocations;
			state.totalPages = initialState.totalPages;
		},
	},
});

export const thunks = {
	syscoManageLocationsPageLoaded(customerId: number) {
		return async (dispatch, getState) => {
			dispatch(slice.actions.resetState());
			dispatch(slice.actions.loadingLocations());

			const state = getState().syscoManageLocations;

			const customerRes = await Hub.CustomerControllerV3.Get(customerId);
			if (!customerRes.ok) {
				dispatch(slice.actions.loadingLocations_ERROR());
				return;
			}
			dispatch(slice.actions.setCustomer(customerRes.value));

			const locationsRes =
				await Hub.ManageLocationsController.GetLocationsForCustomer({
					customerId: customerRes.value.id,
					pageSize: state.itemsPerPage,
					pageIndex: state.currentPage,
				});
			if (!locationsRes.ok) {
				dispatch(slice.actions.loadingLocations_ERROR());
				return;
			}
			dispatch(slice.actions.loadingLocations_SUCCESS(locationsRes.value));
		};
	},
	syscoManageLocationsPageChanged(page: number) {
		return async (dispatch, getState) => {
			dispatch(slice.actions.loadingLocations());
			dispatch(slice.actions.pageChange(page));

			const state = getState().syscoManageLocations;

			const locationsRes =
				await Hub.ManageLocationsController.GetLocationsForCustomer({
					customerId: state.customerId,
					pageSize: state.itemsPerPage,
					pageIndex: state.currentPage,
				});
			if (!locationsRes.ok) {
				dispatch(slice.actions.loadingLocations_ERROR());
				return;
			}
			dispatch(slice.actions.loadingLocations_SUCCESS(locationsRes.value));
		};
	},
	syscoManageLocationsItemsPerPageChanged(itemsPerPage: number) {
		return async (dispatch, getState) => {
			dispatch(slice.actions.loadingLocations());
			dispatch(slice.actions.itemsPerPageChange(itemsPerPage));

			const state = getState().syscoManageLocations;

			const locationsRes =
				await Hub.ManageLocationsController.GetLocationsForCustomer({
					customerId: state.customerId,
					pageSize: state.itemsPerPage,
					pageIndex: state.currentPage,
				});
			if (!locationsRes.ok) {
				dispatch(slice.actions.loadingLocations_ERROR());
				return;
			}
			dispatch(slice.actions.loadingLocations_SUCCESS(locationsRes.value));
		};
	},
	syscoManageLocationsRowSelected(args: {
		location: IManageLocations;
		selected: boolean;
	}) {
		return async (dispatch) => {
			if (args.selected) {
				dispatch(slice.actions.addSelectedLocationId(args.location.id));
			} else {
				dispatch(slice.actions.removeSelectedLocationId(args.location.id));
			}
		};
	},
	syscoManageLocationsOpenDeleteModal() {
		return async (dispatch) => {
			dispatch(slice.actions.openDeleteModal());
		};
	},
	syscoManageLocationsCloseDeleteModal() {
		return async (dispatch) => {
			dispatch(slice.actions.closeDeleteModal());
		};
	},
	syscoManageLocationsDeleteSelectedLocations() {
		return async (dispatch, getState) => {
			dispatch(slice.actions.loadingLocations());
			dispatch(slice.actions.setIsDeleting(true));

			let state = getState().syscoManageLocations;

			const deleteRes = await Hub.DeleteSyscoLocationsV1.DeleteSyscoLocations({
				customerId: state.customerId,
				locationIds: state.selectedLocationIds,
			});
			if (!deleteRes.ok) {
				dispatch(slice.actions.loadingLocations_ERROR());
				return;
			}

			dispatch(slice.actions.resetAfterLocationsDeleted());

			state = getState().syscoManageLocations;

			const locationsRes =
				await Hub.ManageLocationsController.GetLocationsForCustomer({
					customerId: state.customerId,
					pageSize: state.itemsPerPage,
					pageIndex: state.currentPage,
				});
			if (!locationsRes.ok) {
				dispatch(slice.actions.loadingLocations_ERROR());
				return;
			}
			dispatch(slice.actions.loadingLocations_SUCCESS(locationsRes.value));
		};
	},
};
